import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { StatusBar, Style } from '@capacitor/status-bar';
import type { GameEvent } from '@horde/engine';

/** True inside the iOS/Android shell; false in a plain browser. */
export const isNative = Capacitor.isNativePlatform();

/** One-time native setup. Safe to call on the web: every call is guarded. */
export async function initNative(): Promise<void> {
  if (!isNative) return;
  try {
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setOverlaysWebView({ overlay: true });
  } catch {
    // plugin missing on this platform
  }
}

/** Keep the screen on while a game is on the table. */
export async function setKeepAwake(on: boolean): Promise<void> {
  if (!isNative) return;
  try {
    if (on) await KeepAwake.keepAwake();
    else await KeepAwake.allowSleep();
  } catch {
    // ignore
  }
}

/** Tactile feedback for the moments that matter. No-op on the web. */
export function hapticsFor(events: GameEvent[], me: string): void {
  if (!isNative) return;
  let kind: 'roll' | 'capture' | 'loss' | 'overrun' | null = null;
  for (const e of events) {
    if (e.type === 'territoryCaptured' && e.to === me) kind = 'capture';
    else if (e.type === 'territoryCaptured' && e.from === me) kind = kind ?? 'loss';
    else if (e.type === 'overrun' && e.previousOwner === me) kind = 'overrun';
    else if (e.type === 'gameEnded') kind = e.outcome.kind === 'horde' ? 'overrun' : 'capture';
    else if (e.type === 'diceRolled' && !kind) kind = 'roll';
  }
  if (!kind) return;
  void (async () => {
    try {
      if (kind === 'roll') await Haptics.impact({ style: ImpactStyle.Light });
      else if (kind === 'capture') await Haptics.notification({ type: NotificationType.Success });
      else if (kind === 'loss') await Haptics.impact({ style: ImpactStyle.Medium });
      else await Haptics.notification({ type: NotificationType.Error });
    } catch {
      // ignore
    }
  })();
}

/** Called when the app is backgrounded/foregrounded; used to pause bot turns. */
export function onAppStateChange(cb: (active: boolean) => void): () => void {
  if (!isNative) return () => {};
  const handle = CapApp.addListener('appStateChange', ({ isActive }) => cb(isActive));
  return () => {
    void handle.then((h) => h.remove());
  };
}
